import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle, LayoutGrid, List, FileText, X } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Badge } from '../../../../../components/ui/Badge';
import { FilterBar, ExportMenu, MODULE_CONFIG } from '../shared';
import { Select } from '../../../../../components/ui/Select';
import { QuestionDetailPanel } from './QuestionDetailPanel';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
import type { IssueOwner, PipelineBatchResult } from '../../../../../governance';
import { deriveDisplaySeverity, resolveIssueOwnership } from '../../../../../governance';
import { QUESTION_TYPE_REGISTRY } from '../../../../../content/questions/questionTypeRegistry';
/**
 * Look up the human-readable Dutch label for a question type.
 * Falls back to "Onbekend" when the type is the 'unknown' sentinel
 * emitted by buildUnifiedRows (question lookup miss), or returns
 * the raw key when a registry entry is missing (defensive fallback).
 */
function getQuestionTypeLabel(type: string): string {
  if (type === 'unknown') return 'Onbekend';
  const entry = (QUESTION_TYPE_REGISTRY as Record<string, {
    label?: string;
  }>)[type];
  return entry?.label ?? type;
}
interface QuestionsTabProps {
  rows: UnifiedQuestionRow[];
  batchResult: PipelineBatchResult | null;
  isRunning: boolean;
  initialFilter?: string;
  onOpenQuestionInBank?: (row: UnifiedQuestionRow) => void;
}
type VerdictFilter = 'all' | 'PASS' | 'REVIEW_NEEDED' | 'FAIL';
type IssueFilter = 'all' | 'critical' | 'blockers' | 'review' | 'warnings' | 'info';
type ViewMode = 'cards' | 'table';
type OwnerFilter = 'all' | IssueOwner;
export function QuestionsTab({
  rows,
  batchResult,
  isRunning,
  initialFilter,
  onOpenQuestionInBank
}: QuestionsTabProps) {
  const [search, setSearch] = useState(initialFilter && initialFilter !== 'blockers' ? initialFilter : '');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>(initialFilter === 'blockers' ? 'FAIL' : 'all');
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('all');
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  // Quick filters
  const [onlySeedIssues, setOnlySeedIssues] = useState(false);
  const [onlyVisualIssues, setOnlyVisualIssues] = useState(false);
  const [onlyPedagogyIssues, setOnlyPedagogyIssues] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;
  useEffect(() => {
    setSearch(initialFilter && initialFilter !== 'blockers' ? initialFilter : '');
    setVerdictFilter(initialFilter === 'blockers' ? 'FAIL' : 'all');
    setExpandedId(initialFilter && initialFilter !== 'blockers' ? rows.some(row => row.questionId === initialFilter) ? initialFilter : null : null);
  }, [initialFilter, rows]);
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      // Search
      if (search && !row.questionId.toLowerCase().includes(search.toLowerCase()) && !row.familyId.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      // Module
      if (moduleFilter !== 'all' && row.moduleCode !== moduleFilter) {
        return false;
      }
      // Verdict
      if (verdictFilter !== 'all' && row.verdict !== verdictFilter) {
        return false;
      }
      // Issue Type — Phase 1: 5-level display severity filter
      if (issueFilter !== 'all') {
        const hasMatching = row.pipelineIssues.some(i => {
          const ds = deriveDisplaySeverity(i.kind, i.severity);
          if (issueFilter === 'critical') return ds === 'critical';
          if (issueFilter === 'blockers') return ds === 'critical' || ds === 'blocker';
          if (issueFilter === 'review') return ds === 'review';
          if (issueFilter === 'warnings') return ds === 'coach';
          if (issueFilter === 'info') return ds === 'info';
          return false;
        });
        if (!hasMatching) return false;
      }
      // Remediation owner: a row stays visible when at least one finding
      // belongs to the selected route.
      if (ownerFilter !== 'all' && !row.pipelineIssues.some(issue => resolveIssueOwnership(issue).owner === ownerFilter)) {
        return false;
      }
      // Question Type
      if (typeFilter !== 'all' && row.questionType !== typeFilter) {
        return false;
      }
      // Quick Filters
      if (onlySeedIssues && row.seedWarnings.length === 0) return false;
      if (onlyVisualIssues && !row.hasVisualProblems) return false;
      if (onlyPedagogyIssues && !row.pipelineIssues.some(i => i.domain === 'pedagogy')) return false;
      return true;
    });
  }, [rows, search, moduleFilter, verdictFilter, issueFilter, ownerFilter, typeFilter, onlySeedIssues, onlyVisualIssues, onlyPedagogyIssues]);
  // Detect "scoped to single seed file" — happens when user deep-links from
  // the Seed Prioritizer card on the Overzicht tab. We surface this clearly
  // so the admin knows they're currently triaging one seed at a time.
  const familyContext = useMemo(() => {
    if (!search || filteredRows.length === 0) return null;
    const uniqueFamilies = new Set(filteredRows.map(r => r.familyId));
    if (uniqueFamilies.size !== 1) return null;
    const familyId = filteredRows[0].familyId;
    // Only show the banner if the search term actually targets the family
    // (case-insensitive substring match). Otherwise this is just coincidence.
    if (!familyId.toLowerCase().includes(search.toLowerCase())) return null;
    const blockerCount = filteredRows.reduce((sum, r) => sum + r.blockerCount, 0);
    const warningCount = filteredRows.reduce((sum, r) => sum + r.warningCount, 0);
    const failCount = filteredRows.filter(r => r.verdict === 'FAIL').length;
    return {
      familyId,
      moduleCode: filteredRows[0].moduleCode,
      total: filteredRows.length,
      failCount,
      blockerCount,
      warningCount
    };
  }, [filteredRows, search]);
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, moduleFilter, verdictFilter, issueFilter, ownerFilter, typeFilter, onlySeedIssues, onlyVisualIssues, onlyPedagogyIssues]);
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, page]);
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const passCount = filteredRows.filter(r => r.verdict === 'PASS').length;
  const failCount = filteredRows.filter(r => r.verdict === 'FAIL').length;
  const reviewCount = filteredRows.filter(r => r.verdict === 'REVIEW_NEEDED').length;
  // Build type-filter options from the registry, sorted by Dutch label, and
  // include an "Onbekend" option ONLY when at least one row actually has the
  // 'unknown' fallback — that way the user can find broken rows without the
  // dropdown showing a phantom option in healthy datasets.
  const typeOptions = useMemo(() => {
    const registryEntries = Object.entries(QUESTION_TYPE_REGISTRY as Record<string, {
      label?: string;
    }>).map(([value, cfg]) => ({
      value,
      label: cfg.label ?? value
    })).sort((a, b) => a.label.localeCompare(b.label, 'nl'));
    const hasUnknown = rows.some(r => r.questionType === 'unknown');
    const options = [{
      value: 'all',
      label: 'Alle vraagtypes'
    }, ...registryEntries];
    if (hasUnknown) options.push({
      value: 'unknown',
      label: 'Onbekend'
    });
    return options;
  }, [rows]);
  if (isRunning) {
    return <div className="py-12 flex flex-col items-center justify-center space-y-4" data-id="element-5571" data-mp="1t0bm0wle955y">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" data-id="element-5572" data-mp="15ahb4kkmxqoc"></div>
        <p className="text-gray-500 font-medium" data-id="element-5573" data-mp="10bo8xoastm3h">Data laden en valideren...</p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-5574" data-mp="1ebp9f2n5irif">
      {/* Family-scope banner — visible when filtered to a single seed file */}
      {familyContext && <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-orange-200 bg-orange-50/70" data-id="element-5575" data-mp="2ppaxekh9yn2d">
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${MODULE_CONFIG[familyContext.moduleCode]?.bgClass ?? 'bg-gray-400'}`} data-id="element-5576" data-mp="2kqgdtgbhguh7" />
          <FileText className="w-4 h-4 text-orange-600 flex-shrink-0" data-id="element-5577" data-mp="2t5xd079juqrl" />
          <div className="min-w-0 flex-1" data-id="element-5578" data-mp="2x9limkemfca7">
            <p className="text-sm font-semibold text-orange-900 truncate" data-id="element-5579" data-mp="2x6h8l6eh5mbm">
              Bekijk seed:{' '}
              <span className="font-mono" data-id="element-5580" data-mp="2o846fd4c3uqs">{familyContext.familyId}</span>
            </p>
            <p className="text-xs text-orange-800/80 mt-0.5" data-id="element-5581" data-mp="2o9yq6o3vdgta">
              {familyContext.total}{' '}
              {familyContext.total === 1 ? 'vraag' : 'vragen'}
              {familyContext.failCount > 0 && ` · ${familyContext.failCount} afgekeurd`}
              {familyContext.blockerCount > 0 && ` · ${familyContext.blockerCount} blocker${familyContext.blockerCount === 1 ? '' : 's'}`}
              {familyContext.warningCount > 0 && ` · ${familyContext.warningCount} verbeterpunt${familyContext.warningCount === 1 ? '' : 'en'}`}
            </p>
          </div>
          <button type="button" onClick={() => setSearch('')} className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-700 hover:text-orange-900 bg-white border border-orange-200 hover:bg-orange-100 rounded-lg transition-colors flex-shrink-0" data-id="element-5582" data-mp="24nky4q0r2g9y">
            <X className="w-3 h-3" data-id="element-5583" data-mp="20l824tbt1wqd" />
            Wis filter
          </button>
        </div>}

      {/* Filters */}
      <FilterBar moduleFilter={moduleFilter} onModuleFilter={setModuleFilter} search={search} onSearch={setSearch} data-id="element-5584" data-mp="1lw1vwlejv68z">
        <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} options={typeOptions} size="sm" fullWidth={false} data-id="element-5585" data-mp="1cw5glae1vjql" />
        <Select value={verdictFilter} onChange={e => setVerdictFilter(e.target.value as VerdictFilter)} options={[{
        value: 'all',
        label: 'Alle statussen'
      }, {
        value: 'PASS',
        label: 'Geslaagd (PASS)'
      }, {
        value: 'REVIEW_NEEDED',
        label: 'Review (REVIEW)'
      }, {
        value: 'FAIL',
        label: 'Afgekeurd (FAIL)'
      }]} size="sm" fullWidth={false} data-id="element-5586" data-mp="12xzdipgic7k5" />
        <Select value={issueFilter} onChange={e => setIssueFilter(e.target.value as IssueFilter)} options={[{
        value: 'all',
        label: 'Alle bevindingen'
      }, {
        value: 'critical',
        label: '🔴 Heeft kritieke'
      }, {
        value: 'blockers',
        label: '🟠 Heeft blockers'
      }, {
        value: 'review',
        label: '🟡 Heeft review'
      }, {
        value: 'warnings',
        label: '🔵 Heeft coach'
      }, {
        value: 'info',
        label: '⚪ Heeft info'
      }]} size="sm" fullWidth={false} data-id="element-5587" data-mp="179mpwh4jxid1" />
        <Select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value as OwnerFilter)} options={[{
        value: 'all',
        label: 'Alle eigenaren'
      }, {
        value: 'seed',
        label: 'Seed'
      }, {
        value: 'rulebook',
        label: 'Rulebook'
      }, {
        value: 'platform-registry',
        label: 'Platform/registry'
      }, {
        value: 'review-first',
        label: 'Eerst beoordelen'
      }]} size="sm" fullWidth={false} data-id="element-5588" data-mp="23054cx1i04ra" />
      </FilterBar>

      {/* Quick Filters & Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm" data-id="element-5589" data-mp="39m82235jxrcj">
        <div className="flex flex-wrap items-center gap-2" data-id="element-5590" data-mp="34ugjofwjurxh">
          <button onClick={() => setOnlySeedIssues(!onlySeedIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlySeedIssues ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5591" data-mp="30tocrtb5gz4d">
            Seed Issues
          </button>
          <button onClick={() => setOnlyVisualIssues(!onlyVisualIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlyVisualIssues ? 'bg-purple-100 text-purple-800 border-purple-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5592" data-mp="3oka6sgobkivd">
            Visual Issues
          </button>
          <button onClick={() => setOnlyPedagogyIssues(!onlyPedagogyIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlyPedagogyIssues ? 'bg-indigo-100 text-indigo-800 border-indigo-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-5593" data-mp="2354jzoihw06i">
            Pedagogie Issues
          </button>

          <div className="h-4 w-px bg-gray-300 mx-1 hidden sm:block" data-id="element-5594" data-mp="3n0h1bhtcwulq"></div>

          <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-id="element-5595" data-mp="elgrk3xzi8mt">
            <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Tabelweergave" data-id="element-5596" data-mp="ejcdwuaa9v9c">
              <List className="w-4 h-4" data-id="element-5597" data-mp="9kj1l56qgaar" />
            </button>
            <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Kaartweergave" data-id="element-5598" data-mp="2pd8jbwske3oh">
              <LayoutGrid className="w-4 h-4" data-id="element-5599" data-mp="2hu8z5oxa4842" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm" data-id="element-5600" data-mp="2vti5zxzlhpvd">
          <div className="flex items-center gap-3 text-gray-600" data-id="element-5601" data-mp="2vsyxt8zqyfuj">
            <span className="font-semibold text-gray-900" data-id="element-5602" data-mp="2qvqhldy5slvx">
              {filteredRows.length}
            </span>{' '}
            vragen
            <span className="text-emerald-600 font-medium" data-id="element-5603" data-mp="23307795wmanr">
              {passCount} geslaagd
            </span>
            <span className="text-red-600 font-medium" data-id="element-5604" data-mp="1z1of8wzk5dce">
              {failCount} afgekeurd
            </span>
            <span className="text-amber-600 font-medium" data-id="element-5605" data-mp="284fr2xwcib4k">
              {reviewCount} review
            </span>
          </div>

          {batchResult && <ExportMenu results={batchResult.results.filter(r => filteredRows.some(fr => fr.questionId === r.questionId))} filePrefix="governance-vragen" data-id="element-5606" data-mp="1kcs76zxi40bs" />}
        </div>
      </div>

      {/* Table / Cards */}
      <div className="space-y-2" data-id="element-5607" data-mp="3kf9w0l04di8k">
        {paginatedRows.length === 0 ? <div className="rounded-xl border border-gray-200 bg-white py-12 text-center" data-id="element-5608" data-mp="3p8m8xxvfs0lh">
            <p className="text-gray-500" data-id="element-5609" data-mp="3u6xhm59zw524">
              Geen vragen gevonden voor deze filters.
            </p>
          </div> : viewMode === 'table' ? <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5610" data-mp="31hywr0ehmot4">
            <div className="overflow-x-auto" data-id="element-5611" data-mp="36h355otl8iie">
              <table className="w-full text-left text-sm" data-id="element-5612" data-mp="36g0ijt3at28a">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-medium" data-id="element-5613" data-mp="3aiombmd5niye">
                  <tr data-id="element-5614" data-mp="lqui4mt1dmov">
                    <th className="px-4 py-3" data-id="element-5615" data-mp="lrm3l4sx641f">ID</th>
                    <th className="px-4 py-3" data-id="element-5616" data-mp="lsxbngtedxyc">Status</th>
                    <th className="px-4 py-3" data-id="element-5617" data-mp="prejk2kn7cgt">Score</th>
                    <th className="px-4 py-3" data-id="element-5618" data-mp="2aagv6x4fuk4k">Issues</th>
                    <th className="px-4 py-3" data-id="element-5619" data-mp="2a9yqrd7mgzlk">Type</th>
                    <th className="px-4 py-3" data-id="element-5620" data-mp="269556qhnjwvu">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-5621" data-mp="21hn1vvs5pk7p">
                  {paginatedRows.map(row => {
                const isExpanded = expandedId === row.questionId;
                const modConfig = MODULE_CONFIG[row.moduleCode] || {
                  bgClass: 'bg-gray-500'
                };
                const statusConfig = {
                  PASS: {
                    bg: 'bg-emerald-50',
                    text: 'text-emerald-700',
                    icon: CheckCircle2,
                    label: 'PASS'
                  },
                  REVIEW_NEEDED: {
                    bg: 'bg-amber-50',
                    text: 'text-amber-700',
                    icon: AlertTriangle,
                    label: 'REVIEW'
                  },
                  FAIL: {
                    bg: 'bg-red-50',
                    text: 'text-red-700',
                    icon: XCircle,
                    label: 'FAIL'
                  }
                }[row.verdict];
                const StatusIcon = statusConfig.icon;
                return <Fragment key={row.questionId} data-id="element-5622" data-mp="1y2eq98ubd4oz">
                        <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/30' : ''}`} onClick={() => setExpandedId(isExpanded ? null : row.questionId)} data-id="element-5623" data-mp="1y1loyne4j7e8">
                          <td className="px-4 py-3" data-id="element-5624" data-mp="1t5emx5oumg1k">
                            <div className="flex items-center gap-2" data-id="element-5625" data-mp="3ij9zkpwwe8qy">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-5626" data-mp="3nh31v7v2yily" />
                              <span className="font-mono font-semibold text-gray-900" data-id="element-5627" data-mp="1brvkrtnhnh19">
                                {row.questionId}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5628" data-mp="1gsj8o6gf7hr9">
                            <Badge variant={row.verdict === 'PASS' ? 'success' : row.verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-5629" data-mp="1grrk3fl8qkah">
                              <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-5630" data-mp="a447g4llh1x2" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3" data-id="element-5631" data-mp="1yala1ttlbgi2">
                            <div className="flex items-center gap-2 w-24" data-id="element-5632" data-mp="1y9a1lgp79kkm">
                              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5633" data-mp="1taqjxshn2uy2">
                                <div className={`h-full rounded-full ${row.pipelineScore >= 80 ? 'bg-emerald-500' : row.pipelineScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                            width: `${row.pipelineScore}%`
                          }} data-id="element-5634" data-mp="27zcvdj45b2k0" />
                              </div>
                              <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-5635" data-mp="1pbhpsmiosv1z">
                                {row.pipelineScore}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5636" data-mp="2vqs5hy25yo5t">
                            <div className="flex items-center gap-1.5 text-xs" data-id="element-5637" data-mp="2vscvdadj9ixc">
                              {row.blockerCount > 0 && <span className="flex items-center gap-1 text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded" data-id="element-5638" data-mp="389dym368vdt8">
                                  <XCircle className="w-3 h-3" data-id="element-5639" data-mp="2uaehrhoz437m" />{' '}
                                  {row.blockerCount}
                                </span>}
                              {row.warningCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-5640" data-mp="3qz2pnl6z6a3b">
                                  <AlertTriangle className="w-3 h-3" data-id="element-5641" data-mp="3d6w6dq8izmzn" />{' '}
                                  {row.warningCount}
                                </span>}
                              {row.blockerCount === 0 && row.warningCount === 0 && <span className="text-gray-400" data-id="element-5642" data-mp="dm0qz5rhwul0">-</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-5643" data-mp="jekxahj55k8p">
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${row.questionType === 'unknown' ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-gray-600 bg-gray-50 border-gray-200'}`} title={row.questionType === 'unknown' ? 'Vraag niet gevonden in content registry — mogelijk verouderde batch.' : row.questionType} data-id="element-5644" data-mp="jd05v0s4ihf2">
                              {getQuestionTypeLabel(row.questionType)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-5645" data-mp="3hyb7debl8ax0">
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 inline" data-id="element-5646" data-mp="32tp5pleis1j1" /> : <ChevronRight className="w-4 h-4 text-gray-400 inline" data-id="element-5647" data-mp="37rqoe5819ytz" />}
                          </td>
                        </tr>
                        {isExpanded && <tr data-id="element-5648" data-mp="3hpo7keb1d5fq">
                            <td colSpan={6} className="p-0 border-t border-gray-100" data-id="element-5649" data-mp="3homygfdkyt0n">
                              <QuestionDetailPanel row={row} onOpenQuestionInBank={onOpenQuestionInBank} data-id="element-5650" data-mp="2p2tq7q9imm6h" />
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>
          </div> : paginatedRows.map(row => {
        const isExpanded = expandedId === row.questionId;
        const modConfig = MODULE_CONFIG[row.moduleCode] || {
          bgClass: 'bg-gray-500'
        };
        const statusConfig = {
          PASS: {
            bg: 'bg-emerald-50',
            border: 'border-emerald-200',
            text: 'text-emerald-700',
            icon: CheckCircle2,
            label: 'PASS'
          },
          REVIEW_NEEDED: {
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            text: 'text-amber-700',
            icon: AlertTriangle,
            label: 'REVIEW'
          },
          FAIL: {
            bg: 'bg-red-50',
            border: 'border-red-200',
            text: 'text-red-700',
            icon: XCircle,
            label: 'FAIL'
          }
        }[row.verdict];
        const StatusIcon = statusConfig.icon;
        return <Card key={row.questionId} className={`overflow-hidden transition-colors border ${isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-5651" data-mp="1gvab5x7yhz6d">
                <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer bg-white" onClick={() => setExpandedId(isExpanded ? null : row.questionId)} data-id="element-5652" data-mp="1745wkym70gc2">
                  {/* Left: ID & Status */}
                  <div className="flex items-center gap-3 min-w-0" data-id="element-5653" data-mp="o5tj50o90kao">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-5654" data-mp="o7o7yq2n4wk1" />
                    <div className="flex items-center gap-3 min-w-0" data-id="element-5655" data-mp="2ij2yva7e121d">
                      <span className="font-mono text-sm font-semibold text-gray-900 truncate" data-id="element-5656" data-mp="2ihrvhbhutwas">
                        {row.questionId}
                      </span>
                      <Badge variant={row.verdict === 'PASS' ? 'success' : row.verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-5657" data-mp="2m33fa0soh6vx">
                        <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-5658" data-mp="34qymf1tsbmg2" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Right: Score & Issues */}
                  <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0" data-id="element-5659" data-mp="2v03x1ol8s6qe">
                    {/* Issues Summary */}
                    <div className="flex items-center gap-2 text-xs" data-id="element-5660" data-mp="3ni9bddfy9p5b">
                      {row.blockerCount > 0 && <span className="flex items-center gap-1 text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded" data-id="element-5661" data-mp="3irj7y5r33wd9">
                          <XCircle className="w-3 h-3" data-id="element-5662" data-mp="15apug271bo5u" /> {row.blockerCount}
                        </span>}
                      {row.warningCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-5663" data-mp="1e8rzqtxsx2yx">
                          <AlertTriangle className="w-3 h-3" data-id="element-5664" data-mp="1ja7oox33ntml" />{' '}
                          {row.warningCount}
                        </span>}
                      {row.seedWarnings.length > 0 && <span className="flex items-center gap-1 text-purple-600 font-medium bg-purple-50 px-1.5 py-0.5 rounded" title="Seed warnings" data-id="element-5665" data-mp="1xxsooi4ohcqa">
                          🌱 {row.seedWarnings.length}
                        </span>}
                    </div>

                    {/* Score Bar */}
                    <div className="hidden sm:flex items-center gap-2 w-24" data-id="element-5666" data-mp="1mug099is1f42">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5667" data-mp="1mwj0snhk2qn1">
                        <div className={`h-full rounded-full ${row.pipelineScore >= 80 ? 'bg-emerald-500' : row.pipelineScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${row.pipelineScore}%`
                  }} data-id="element-5668" data-mp="1hv3b2i4fnvyb" />
                      </div>
                      <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-5669" data-mp="z5wv32v0xig7">
                        {row.pipelineScore}%
                      </span>
                    </div>

                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-5670" data-mp="1848o1bjd4sbt" /> : <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-5671" data-mp="ke4q9ksuvmsq" />}
                  </div>
                </div>

                {/* Expanded Detail Panel */}
                {isExpanded && <QuestionDetailPanel row={row} onOpenQuestionInBank={onOpenQuestionInBank} data-id="element-5672" data-mp="68kt00hok1ll" />}
              </Card>;
      })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200" data-id="element-5673" data-mp="19899om3van8">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5674" data-mp="u000e7obd93p">
            Vorige
          </button>
          <span className="text-sm text-gray-600" data-id="element-5675" data-mp="2zrwyxqbnc2h5">
            Pagina <span className="font-semibold text-gray-900" data-id="element-5676" data-mp="34s1jpsjl7jd6">{page}</span>{' '}
            van {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5677" data-mp="22cbxvzk0vk5r">
            Volgende
          </button>
        </div>}
    </div>;
}